import React, { useState, useEffect, useMemo } from 'react';
import { formatMontant, getDeviseCourante } from '../../utils/format';
import {
  AlertTriangle, CheckCircle, MessageSquare, Layers, Package, ChevronDown, ChevronRight,
} from 'lucide-react';
import api from '../../services/api';
import { Button, Select } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';

// ──────────────────────────────────────────────────────────────
// FormClotureCumulee — clôture en une seule opération de TOUTES
// les sorties ouvertes du jour pour un vendeur.
//
// Règle d'écart v2 (identique au mode par-sortie, mais agrégée) :
//   Écart = montant_remis_total + valeur_retournée_totale − valeur_sortie_totale
// ──────────────────────────────────────────────────────────────

export default function FormClotureCumulee({ vendeur, onSuccess, onCancel }) {
  const [sortiesOuvertes, setSortiesOuvertes] = useState([]); // [{sortie, lignes:[{...avec quantite_retournee}]}]
  const [chargement, setChargement] = useState(true);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [montantRemis, setMontantRemis] = useState('');
  const [modePaiement, setModePaiement] = useState('especes');
  const [explication, setExplication] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const dateStr = new Date().toISOString().split('T')[0];

  // ── Charger les sorties ouvertes du jour ──────────────────────
  useEffect(() => {
    api.get(`/vendeurs/${vendeur.id}/sorties-jour`, { params: { date: dateStr } })
      .then(({ data }) => {
        const ouvertes = (data || [])
          .filter(s => s.statut === 'ouverte' && !s.retour_id)
          .map(s => ({
            ...s,
            lignes: (s.lignes || []).map(l => ({
              sortie_ligne_id: l.id,
              variante_id: l.variante_id,
              produit_nom: l.produit_nom,
              modele: l.modele,
              taille: l.taille || '',
              couleur: l.couleur,
              quantite_confiee: parseInt(l.quantite_confiee),
              prix_vente_unitaire: parseFloat(l.prix_vente_unitaire),
              // Par défaut : rien n'est retourné (le vendeur ajuste à la hausse)
              quantite_retournee: 0,
            })),
          }));
        setSortiesOuvertes(ouvertes);
        // On déplie la première sortie pour montrer l'UX
        if (ouvertes.length > 0) setExpandedIds(new Set([ouvertes[0].id]));
      })
      .catch(() => toast.error('Erreur chargement sorties'))
      .finally(() => setChargement(false));
  }, [vendeur.id, dateStr]);

  // ── Toggle expansion d'une sortie ─────────────────────────────
  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Mettre à jour une quantité retournée ──────────────────────
  const mettreAJourRetour = (sortieId, ligneIdx, val) => {
    setSortiesOuvertes(prev => prev.map(s => {
      if (s.id !== sortieId) return s;
      const lignes = s.lignes.map((l, idx) => {
        if (idx !== ligneIdx) return l;
        const max = l.quantite_confiee;
        const v = Math.min(Math.max(0, parseInt(val) || 0), max);
        return { ...l, quantite_retournee: v };
      });
      return { ...s, lignes };
    }));
  };

  // ── Tout retourner / tout vendre sur une sortie ───────────────
  const toutRetourner = (sortieId) => {
    setSortiesOuvertes(prev => prev.map(s =>
      s.id === sortieId
        ? { ...s, lignes: s.lignes.map(l => ({ ...l, quantite_retournee: l.quantite_confiee })) }
        : s
    ));
  };
  const toutVendre = (sortieId) => {
    setSortiesOuvertes(prev => prev.map(s =>
      s.id === sortieId
        ? { ...s, lignes: s.lignes.map(l => ({ ...l, quantite_retournee: 0 })) }
        : s
    ));
  };

  // ── Calculs temps réel globaux ────────────────────────────────
  const totaux = useMemo(() => {
    let valeurSortie = 0, valeurRetournee = 0, piecesConfiees = 0, piecesRetournees = 0;
    for (const s of sortiesOuvertes) {
      for (const l of s.lignes) {
        valeurSortie    += l.quantite_confiee  * l.prix_vente_unitaire;
        valeurRetournee += l.quantite_retournee * l.prix_vente_unitaire;
        piecesConfiees  += l.quantite_confiee;
        piecesRetournees += l.quantite_retournee;
      }
    }
    return {
      valeurSortie,
      valeurRetournee,
      valeurVendue: valeurSortie - valeurRetournee,
      piecesConfiees,
      piecesRetournees,
      piecesVendues: piecesConfiees - piecesRetournees,
    };
  }, [sortiesOuvertes]);

  const montantRemisNum = parseFloat(montantRemis) || 0;
  const aSaisiMontant   = montantRemis !== '' && !isNaN(montantRemisNum);
  const ecart           = montantRemisNum + totaux.valeurRetournee - totaux.valeurSortie;

  const ecartPositif        = ecart > 0;
  const ecartNegatifNonExpl = ecart < 0 && !explication.trim();
  const submitBloque        = !aSaisiMontant
                              || ecartPositif
                              || ecartNegatifNonExpl
                              || sortiesOuvertes.length === 0;

  const handleSubmit = async () => {
    if (submitBloque) return;
    setLoading(true);
    try {
      const sorties_retour = sortiesOuvertes.map(s => ({
        sortie_id: s.id,
        lignes_retour: s.lignes.map(l => ({
          sortie_ligne_id: l.sortie_ligne_id,
          variante_id: l.variante_id,
          quantite_retournee: l.quantite_retournee,
        })),
      }));
      const res = await api.post(`/vendeurs/${vendeur.id}/cloture-cumulee`, {
        date: dateStr,
        sorties_retour,
        montant_remis_total: montantRemisNum,
        mode_paiement_retour: modePaiement,
        explication_ecart: explication || null,
        notes: notes || null,
      });
      if (res.data.dette_ajoutee > 0) {
        toast(`Écart de ${formatMontant(Math.abs(ecart))} ajouté à la dette de ${vendeur.prenom}`, {
          duration: 5000, icon: '⚠️',
        });
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────
  if (chargement) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (sortiesOuvertes.length === 0) {
    return (
      <div className="text-center py-10">
        <Package size={36} className="text-gray-700 mx-auto mb-3" />
        <p className="text-gray-400">
          Aucune sortie ouverte à clôturer pour {vendeur.prenom} {vendeur.nom}.
        </p>
        <p className="text-gray-600 text-xs mt-2">
          Toutes les sorties du jour ont déjà été clôturées individuellement.
        </p>
        <Button variant="secondary" onClick={onCancel} className="mt-5">Fermer</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Layers size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-semibold">
              Clôture cumulée — {vendeur.prenom} {vendeur.nom}
            </p>
            <p className="text-gray-400 text-sm mt-0.5">
              {sortiesOuvertes.length} tournée{sortiesOuvertes.length > 1 ? 's' : ''} ouverte{sortiesOuvertes.length > 1 ? 's' : ''} à clôturer en une fois — {dateStr}
            </p>
          </div>
        </div>
      </div>

      {/* Liste des sorties (accordéon) */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
          Articles confiés ({sortiesOuvertes.length} tournée{sortiesOuvertes.length > 1 ? 's' : ''})
        </h3>
        {sortiesOuvertes.map((s) => {
          const expanded = expandedIds.has(s.id);
          const sortieValeurSortie    = s.lignes.reduce((acc, l) => acc + l.quantite_confiee  * l.prix_vente_unitaire, 0);
          const sortieValeurRetournee = s.lignes.reduce((acc, l) => acc + l.quantite_retournee * l.prix_vente_unitaire, 0);
          const sortieValeurVendue    = sortieValeurSortie - sortieValeurRetournee;
          return (
            <div key={s.id} className="bg-gray-800 rounded-xl overflow-hidden">
              {/* Header sortie */}
              <button
                type="button"
                onClick={() => toggleExpand(s.id)}
                className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                  <span className="text-white font-medium text-sm">
                    Tournée #{s.numero_tournee || 1}
                  </span>
                  <span className="text-gray-500 text-xs">
                    · {s.lignes.length} article{s.lignes.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-yellow-400">Sortie : {formatMontant(sortieValeurSortie)}</span>
                  <span className="text-green-400">Vendu : {formatMontant(sortieValeurVendue)}</span>
                </div>
              </button>

              {/* Contenu plié/déplié */}
              {expanded && (
                <div className="px-3 pb-3 border-t border-gray-700/60 space-y-2 pt-3">
                  {/* Boutons rapides */}
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => toutVendre(s.id)}
                      className="text-xs px-2 py-1 bg-green-700/30 hover:bg-green-700/50 text-green-300 rounded transition-colors"
                    >
                      Tout vendu (0 retour)
                    </button>
                    <button
                      type="button"
                      onClick={() => toutRetourner(s.id)}
                      className="text-xs px-2 py-1 bg-blue-700/30 hover:bg-blue-700/50 text-blue-300 rounded transition-colors"
                    >
                      Tout retourné
                    </button>
                  </div>

                  {s.lignes.map((l, i) => {
                    const qteVendue = l.quantite_confiee - l.quantite_retournee;
                    return (
                      <div key={i} className="bg-gray-900/60 rounded-lg p-2.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{l.produit_nom}</p>
                            <p className="text-gray-500 text-xs">
                              {l.modele} {l.taille ? `· ${l.taille}` : ''} · {l.couleur} ·
                              {' '}<span className="text-yellow-400">{l.quantite_confiee}</span> confié(s)
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-center">
                              <p className="text-[10px] text-gray-500">Retour</p>
                              <input
                                type="number" min="0" max={l.quantite_confiee}
                                value={l.quantite_retournee === 0 ? '' : l.quantite_retournee}
                                onChange={e => mettreAJourRetour(s.id, i, e.target.value)}
                                placeholder="0"
                                className="w-14 bg-gray-700 border border-gray-600 text-white rounded px-1.5 py-1 text-xs text-center focus:outline-none focus:border-yellow-500"
                              />
                            </div>
                            <div className="text-center min-w-12">
                              <p className="text-[10px] text-gray-500">Vendu</p>
                              <p className={`font-bold text-sm ${qteVendue > 0 ? 'text-green-400' : 'text-gray-600'}`}>
                                {qteVendue}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Récapitulatif global */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Récapitulatif global</h3>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">
            Valeur sortie ({totaux.piecesConfiees} pcs sur {sortiesOuvertes.length} tournée{sortiesOuvertes.length > 1 ? 's' : ''})
          </span>
          <span className="text-yellow-400 font-medium">{formatMontant(totaux.valeurSortie)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Valeur retournée ({totaux.piecesRetournees} pcs)</span>
          <span className="text-blue-400 font-medium">{formatMontant(totaux.valeurRetournee)}</span>
        </div>
        <div className="flex items-center justify-between text-sm border-t border-gray-700 pt-2 mt-1">
          <span className="text-gray-200 font-medium">
            Valeur vendue ({totaux.piecesVendues} pcs) — à encaisser
          </span>
          <span className="text-green-400 font-bold text-base">{formatMontant(totaux.valeurVendue)}</span>
        </div>
      </div>

      {/* Montant remis */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
          Argent remis au total
        </h3>
        <div>
          <label className="block text-sm text-gray-400 mb-1.5">Montant remis ({getDeviseCourante()}) *</label>
          <div className="relative">
            <InputMontant
              value={montantRemis}
              onChange={setMontantRemis}
              devise={null}
              placeholder={`À encaisser : ${totaux.valeurVendue.toLocaleString()} ${getDeviseCourante()}`}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 pr-14 text-lg font-bold focus:outline-none focus:border-yellow-500"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold pointer-events-none">{getDeviseCourante()}</span>
          </div>
        </div>
        <Select label="Mode de paiement" value={modePaiement} onChange={e => setModePaiement(e.target.value)}>
          <option value="especes">💵 Espèces</option>
          <option value="orange_money">🟠 Orange Money</option>
          <option value="mtn_money">🟡 MTN Money</option>
          <option value="mixte">🔀 Mixte</option>
        </Select>

        {/* Écart */}
        {aSaisiMontant && (
          <div className={`rounded-xl p-4 ${
            ecart === 0
              ? 'bg-green-900/20 border border-green-700/30'
              : ecart < 0
                ? 'bg-orange-900/30 border border-orange-700/50'
                : 'bg-red-900/40 border border-red-700/60'
          }`}>
            <div className="flex items-start gap-2">
              {ecart === 0
                ? <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                : <AlertTriangle size={18} className={`flex-shrink-0 mt-0.5 ${ecart < 0 ? 'text-orange-400' : 'text-red-400'}`} />
              }
              <div className="flex-1">
                <p className={`font-bold ${
                  ecart === 0 ? 'text-green-400' : ecart < 0 ? 'text-orange-300' : 'text-red-300'
                }`}>
                  Écart global : {ecart >= 0 ? '+' : ''}{formatMontant(ecart)}
                </p>
                {ecart === 0 && (
                  <p className="text-green-300 text-xs mt-0.5">
                    ✓ Équilibre parfait — clôture validable directement
                  </p>
                )}
                {ecart < 0 && (
                  <p className="text-orange-200 text-xs mt-0.5">
                    Le montant remis est inférieur à la valeur vendue.
                    Ce manque sera ajouté à la dette de {vendeur.prenom} {vendeur.nom}.
                    <strong className="block mt-1">Explication obligatoire ci-dessous.</strong>
                  </p>
                )}
                {ecart > 0 && (
                  <p className="text-red-200 text-xs mt-0.5">
                    Le montant remis dépasse la valeur vendue. Vérifiez retours et montant.
                    Un remboursement de dette antérieure se fait via le bouton « Rembourser ».
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Explication conditionnelle */}
        {aSaisiMontant && ecart < 0 && (
          <div>
            <label className="text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
              <MessageSquare size={14} className="text-orange-400" />
              Explication de l'écart <span className="text-red-400">*</span>
            </label>
            <textarea
              value={explication}
              onChange={e => setExplication(e.target.value)}
              placeholder="Ex : oubli, perte, vol, vente à crédit non collectée…"
              rows={2}
              className={`w-full bg-gray-800 border text-white rounded-lg px-3 py-2 text-sm focus:outline-none ${
                !explication.trim()
                  ? 'border-red-700/60 focus:border-red-500'
                  : 'border-gray-700 focus:border-yellow-500'
              }`}
            />
            {!explication.trim() && (
              <p className="text-red-400 text-xs mt-1">
                Une explication est obligatoire pour valider une clôture avec écart.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-400 mb-1.5">Notes (libre)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes optionnelles…"
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
          />
        </div>
      </div>

      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">
          Annuler
        </Button>
        <Button
          loading={loading}
          onClick={handleSubmit}
          disabled={submitBloque}
          className="flex-1"
          variant={ecart < 0 ? 'danger' : 'primary'}
        >
          {ecart < 0
            ? '⚠️ Clôturer avec écart'
            : ecart > 0
              ? '⛔ Montant invalide'
              : `Clôturer ${sortiesOuvertes.length} tournée${sortiesOuvertes.length > 1 ? 's' : ''}`}
        </Button>
      </div>
    </div>
  );
}
